import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function Register() {
  const nav = useNavigate();
  const { sendOtp, login } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: "", phone: "", aadhaar: "", pan: "",
    address: "", nominee_name: "", referral_code_used: "",
  });
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onContinue = async () => {
    if (!form.full_name || !form.phone || form.phone.length < 10) {
      toast.error("Name and 10-digit phone required");
      return;
    }
    setLoading(true);
    try {
      const r = await sendOtp(form.phone, "customer");
      setStep(2);
      setHint(`Dev OTP: ${r.dev_otp}`);
      toast.success(`OTP sent (Dev: ${r.dev_otp})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      // First verify+create user
      const user = await login(form.phone, otp, "customer");
      // Then update customer profile
      await api.patch("/customer/profile", {
        full_name: form.full_name, aadhaar: form.aadhaar,
        pan: form.pan, address: form.address,
        nominee_name: form.nominee_name,
      });
      toast.success("Registration complete!");
      nav("/customer", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="mobile-frame bg-tenant-gradient min-h-screen text-white" data-testid="register-screen">
      <div className="px-6 pt-8 pb-4 flex items-center gap-3">
        <button data-testid="register-back-btn" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl gold-grad grid place-items-center text-tenant-primary">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div className="font-extrabold text-sm tracking-tight">Create Account</div>
        </div>
      </div>

      <div className="bg-white text-slate-900 rounded-t-[2rem] min-h-[calc(100vh-92px)] px-6 py-6">
        {step === 1 ? (
          <div className="space-y-4">
            <Field label="Full Name" testId="reg-name" value={form.full_name} onChange={(v) => set("full_name", v)} placeholder="Enter Full Name" />
            <Field label="Mobile Number" testId="reg-phone" value={form.phone} onChange={(v) => set("phone", v.replace(/\D/g, ""))} placeholder="10-digit mobile" maxLength={10} />
            <Field label="Aadhaar Number" testId="reg-aadhaar" value={form.aadhaar} onChange={(v) => set("aadhaar", v)} placeholder="XXXX-XXXX-XXXX" />
            <Field label="PAN Number (Optional)" testId="reg-pan" value={form.pan} onChange={(v) => set("pan", v.toUpperCase())} placeholder="ABCDE1234F" />
            <Field label="Address" testId="reg-address" value={form.address} onChange={(v) => set("address", v)} placeholder="Your address" />
            <Field label="Nominee Name" testId="reg-nominee" value={form.nominee_name} onChange={(v) => set("nominee_name", v)} placeholder="Nominee full name" />
            <Field label="Referral Code (Optional)" testId="reg-referral" value={form.referral_code_used} onChange={(v) => set("referral_code_used", v.toUpperCase())} placeholder="e.g. RAMESH123" />

            <button data-testid="reg-continue-btn" onClick={onContinue} disabled={loading} className="w-full bg-tenant-primary text-white py-3.5 rounded-2xl font-bold tap-scale disabled:opacity-60">
              {loading ? "Sending OTP..." : "Send OTP & Continue"}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm">OTP sent to <span className="font-bold">+91 {form.phone}</span></div>
            {hint && <div className="text-xs text-emerald-600 font-bold">{hint}</div>}
            <Field label="Enter OTP" testId="reg-otp" value={otp} onChange={(v) => setOtp(v.replace(/\D/g, ""))} placeholder="6-digit OTP" maxLength={6} />
            <button data-testid="reg-submit-btn" onClick={onSubmit} disabled={loading} className="w-full bg-tenant-primary text-white py-3.5 rounded-2xl font-bold tap-scale disabled:opacity-60">
              {loading ? "Creating..." : "Create Account"}
            </button>
            <button data-testid="reg-resend-btn" onClick={() => setStep(1)} className="w-full text-sm text-slate-500">
              Change details
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, testId, value, onChange, placeholder, maxLength }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="mt-1.5 w-full border-2 border-slate-200 rounded-2xl px-4 py-3 focus:border-tenant-primary outline-none"
    />
  </div>
);
